import { Icon, InputGroup, Intent, TextArea } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import Clearfix from 'components/elements/clearfix';
import IconRequired from 'components/elements/required-icon';
import SelectCategory from 'components/elements/select-lookuplist-items';
import { get, isEmpty } from 'lodash';
import { createForm } from 'rc-form';
import React, { useEffect } from 'react';
import { Col, Row } from 'reactstrap';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { COLOR } from 'config';

const ErrorContainer = styled.div`
  margin-top: 5px;
  color: #5c7080;
  font-size: 12px;
  color: red;
`;

const LabelContainer = styled.div`
  align-items: center;
  height: 38px;
  display: flex;
  justify-content: flex-end;
`;

const HeaderContainer = styled.div`
  align-items: center;
  height: 38px;
  display: flex;
  justify-content: flex-start;
  cursor: pointer;
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 18px;
  color: ${COLOR.SECOND_COLOR};
  margin-bottom: 10px;
`;

const FIELDS = {
  Name: 'SpeciesName',
  Type: 'SpeciesType',
  LocalName: 'LocalName',
  ScienceName: 'ScienceName',
  Surname: 'Surname',
  Set: 'Set',
  CharacteristicsAndValues: 'CharacteristicsAndValues',
  ExtinctionThreatLevel: 'ExtinctionThreatLevel',
  ManagementMode: 'ManagementMode',
  Suggestion: 'Suggestion',
  Notes: 'Notes',
};

const LABELS = {
  Name: 'Tên loài',
  Type: 'Mức độ bảo tồn',
  LocalName: 'Tên địa phương',
  ScienceName: 'Tên khoa học',
  Surname: 'Họ',
  Set: 'Bộ',
  CharacteristicsAndValues: 'Đặc điểm và giá trị loài',
  ExtinctionThreatLevel: 'Mức độ bị đe dọa tuyệt chủng',
  ManagementMode: 'Chế độ quản lý, bảo vệ',
  Suggestion: 'Đề xuất, kiến nghị',
  Notes: 'Ghi chú',
};

const FormItem = (props) => {
  const { form, name, label, children, required } = props;
  const { getFieldError } = form || {};

  return (
    <div>
      <Row>
        <Col xs={4}>
          <LabelContainer>
            {label}
            {required ? <IconRequired /> : null}
          </LabelContainer>
        </Col>
        <Col xs={4}>
          {children}

          <ErrorContainer>
            {typeof getFieldError === 'function'
              ? (getFieldError(name) || []).join(', ')
              : null}
          </ErrorContainer>
        </Col>
      </Row>
      <Clearfix height={16} />
    </div>
  );
};

const SpeciesForm = (props) => {
  const { form, initialValue, handleSubmit } = props;
  const { getFieldProps } = form;

  useEffect(() => {
    if (isEmpty(initialValue)) return;

    form.setFieldsValue({
      SpeciesName: {
        value: get(initialValue, 'SpeciesName._id'),
        label: get(initialValue, 'SpeciesName.Name'),
      },
      SpeciesType: {
        value: get(initialValue, 'SpeciesType._id'),
        label: get(initialValue, 'SpeciesType.Name'),
      },
      LocalName: get(initialValue, 'LocalName'),
      ScienceName: get(initialValue, 'ScienceName'),
      Surname: {
        value: get(initialValue, 'Surname._id'),
        label: get(initialValue, 'Surname.Name', '-- Select --'),
      },
      Set: {
        value: get(initialValue, 'Set._id'),
        label: get(initialValue, 'Set.Name', '-- Select --'),
      },
      CharacteristicsAndValues: get(initialValue, 'CharacteristicsAndValues'),
      ExtinctionThreatLevel: get(initialValue, 'ExtinctionThreatLevel'),
      ManagementMode: get(initialValue, 'ManagementMode'),
      Suggestion: get(initialValue, 'Suggestion'),
      Notes: get(initialValue, 'Notes'),
    });
  }, [initialValue]);

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (!values) return;
      const params = {
        SpeciesName: get(values, 'SpeciesName.value'),
        SpeciesType: get(values, 'SpeciesType.value'),
        LocalName: get(values, 'LocalName'),
        ScienceName: get(values, 'ScienceName'),
        Surname: get(values, 'Surname.value', null),
        Set: get(values, 'Set.value', null),
        CharacteristicsAndValues: get(values, 'CharacteristicsAndValues'),
        ExtinctionThreatLevel: get(values, 'ExtinctionThreatLevel'),
        ManagementMode: get(values, 'ManagementMode'),
        Suggestion: get(values, 'Suggestion'),
        Notes: get(values, 'Notes'),
      };
      handleSubmit(params);
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <div>
      <Title>Thông tin loài</Title>
      <Row>
        <Col xs={4}>
          <HeaderContainer>
            <Icon
              icon={IconNames.SAVED}
              iconSize={Icon.SIZE_LARGE}
              intent={Intent.PRIMARY}
              onClick={onSubmit}
              style={{color: COLOR.SUCCESS}}
            />
            <Clearfix width={8} />
            <Icon
              onClick={() => window.history.back()}
              icon={IconNames.DISABLE}
              iconSize={Icon.SIZE_LARGE}
              intent={Intent.DANGER}
            />
          </HeaderContainer>
        </Col>
      </Row>
      <FormItem required label={LABELS.Name} form={form} name={FIELDS.Name}>
        <SelectCategory
          lookupListId='SpiciesName'
          fieldForm={{
            form,
            name: FIELDS.Name,
            option: {
              rules: [
                {
                  validator: (rule, val, callback) => {
                    if (!get(val, 'value')) callback('Bắt buộc nhập tên loài');
                    callback();
                  },
                },
              ],
            },
          }}
        />
      </FormItem>
      <FormItem required label={LABELS.Type} form={form} name={FIELDS.Type}>
        <SelectCategory
          lookupListId='SpiciesType'
          fieldForm={{
            form,
            name: FIELDS.Type,
            option: {
              rules: [
                {
                  validator: (rule, val, callback) => {
                    if (!get(val, 'value'))
                      callback('Bắt buộc nhập mức độ bảo tồn');
                    callback();
                  },
                },
              ],
            },
          }}
        />
      </FormItem>
      <FormItem label={LABELS.LocalName}>
        <InputGroup
          placeholder={LABELS.LocalName}
          {...getFieldProps(FIELDS.LocalName)}
        />
      </FormItem>
      <FormItem label={LABELS.ScienceName} name={FIELDS.ScienceName}>
        <InputGroup
          placeholder={LABELS.ScienceName}
          {...getFieldProps(FIELDS.ScienceName)}
        />
      </FormItem>
      <FormItem label={LABELS.Surname} name={FIELDS.Surname}>
        <SelectCategory
          lookupListId='Surname'
          fieldForm={{ form, name: FIELDS.Surname }}
        />
      </FormItem>
      <FormItem label={LABELS.Set} name={FIELDS.Set}>
        <SelectCategory
          lookupListId='Set'
          fieldForm={{ form, name: FIELDS.Set }}
        />
      </FormItem>
      <FormItem
        label={LABELS.CharacteristicsAndValues}
        name={FIELDS.CharacteristicsAndValues}
      >
        <TextArea
          fill
          placeholder={LABELS.CharacteristicsAndValues}
          {...getFieldProps(FIELDS.CharacteristicsAndValues)}
        />
      </FormItem>
      <FormItem
        label={LABELS.ExtinctionThreatLevel}
        name={FIELDS.ExtinctionThreatLevel}
      >
        <TextArea
          fill
          placeholder={LABELS.ExtinctionThreatLevel}
          {...getFieldProps(FIELDS.ExtinctionThreatLevel)}
        />
      </FormItem>
      <FormItem label={LABELS.ManagementMode} name={FIELDS.ManagementMode}>
        <TextArea
          fill
          placeholder={LABELS.ManagementMode}
          {...getFieldProps(FIELDS.ManagementMode)}
        />
      </FormItem>
      <FormItem label={LABELS.Suggestion} name={FIELDS.Suggestion}>
        <TextArea
          fill
          placeholder={LABELS.Suggestion}
          {...getFieldProps(FIELDS.Suggestion)}
        />
      </FormItem>
      <FormItem label={LABELS.Notes} form={form} name={FIELDS.Notes}>
        <TextArea
          fill
          placeholder={LABELS.Notes}
          {...getFieldProps(FIELDS.Notes)}
        />
      </FormItem>
    </div>
  );
};

export default createForm()(SpeciesForm);

SpeciesForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
};

SpeciesForm.defaultProps = {};



// WEBPACK FOOTER //
// ./src/containers/Biodiversity/BiodiversityView/SpeciesManagement/SpeciesForm/index.js